/**
 * Mission Layer building blocks — drop-in components that enforce the AidWaive
 * promise across every NEW agentic page. None of these touch existing tool pages.
 *
 * Mission: "No student should say no to a college based on price."
 */
import React from "react";

export const MISSION = "No student should say no to a college based on price.";

export const usd = (n) =>
  typeof n === "number" && !Number.isNaN(n) ? `$${Math.round(n).toLocaleString()}` : "—";

/** Permanent mission strip — render at top of every authed page header. */
export function MissionEyebrow({ testId = "mission-eyebrow", subline = null }) {
  return (
    <div className="mb-3" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.25em] text-[#1d4ed8]">The AidWaive promise</p>
      <p className="text-sm italic text-slate-600 mt-1">"{MISSION}"</p>
      {subline && <p className="text-xs text-slate-500 mt-1">{subline}</p>}
    </div>
  );
}

/**
 * Real Cost Reality Check — sticker price always crossed out, net price prominent.
 * Used in Dream School Rescue, Saver action cards, anywhere we surface a number.
 */
export function StickerVsNet({ sticker, net, testId = "real-cost" }) {
  const savings = Math.max(0, (sticker || 0) - (net || 0));
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid={testId}>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sticker price</div>
        <div className="text-xl text-slate-500 line-through" data-testid={`${testId}-sticker`}>
          {usd(sticker)}/yr
        </div>
      </div>
      <div className="rounded-xl border border-[#1d4ed8] bg-[#eff6ff] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#1d4ed8] mb-1">Your real net price</div>
        <div className="font-heading italic text-3xl text-[#1d4ed8]" data-testid={`${testId}-net`}>{usd(net)}/yr</div>
        <div className="text-[11px] text-[#1d4ed8]/80 mt-1">
          What your family would likely actually pay — not the number on the website.
        </div>
      </div>
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Annual savings vs sticker</div>
        <div className="font-heading italic text-3xl text-emerald-700" data-testid={`${testId}-savings`}>{usd(savings)}</div>
      </div>
    </div>
  );
}

/**
 * Sticker Price Blocker — modal shown BEFORE revealing a sticker price > $40k.
 * Caller controls open state; on "Show me the real price first" we call onRevealNet,
 * on "Show sticker anyway" we call onProceed.
 */
export function StickerPriceBlocker({ open, school, sticker, onRevealNet, onProceed }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="sticker-blocker-modal">
      <div className="bg-white rounded-2xl border border-[#bfdbfe] max-w-md w-full p-6 sm:p-8 shadow-xl">
        <p className="text-xs uppercase tracking-[0.25em] text-[#1d4ed8] mb-3">Hold up.</p>
        <h2 className="font-heading italic text-2xl text-[#0f172a] mb-3">
          Before you see this number, know that most students at {school || "this school"} pay far less.
        </h2>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          The sticker price is {usd(sticker)} per year — that's what families with no
          financial need pay. Your real cost is almost certainly lower. Let's find your
          real price first.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onRevealNet}
            data-testid="sticker-blocker-reveal-net"
            className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-5 py-3"
          >
            Show me my real price first
          </button>
          <button
            onClick={onProceed}
            data-testid="sticker-blocker-show-anyway"
            className="text-xs text-slate-500 hover:text-[#0f172a] py-2"
          >
            Show me the sticker anyway
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Don't Rule It Out Flag — confirmation before removing a school from the list.
 * Shown for any school where typical net price for the user's bracket is workable.
 */
export function DontRuleItOutModal({ open, school, typicalNet, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="dont-rule-modal">
      <div className="bg-white rounded-2xl border border-[#bfdbfe] max-w-md w-full p-6 sm:p-8 shadow-xl">
        <h2 className="font-heading italic text-2xl text-[#0f172a] mb-3">
          Before you remove {school}…
        </h2>
        <p className="text-sm text-slate-600 mb-2 leading-relaxed">
          Students at your income level typically pay around{" "}
          <span className="font-medium text-[#1d4ed8]">{usd(typicalNet)}/year</span> at this school
          — not the sticker price.
        </p>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          Want to see your personalized estimate first? It takes 30 seconds.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => onCancel?.("show_estimate")}
            data-testid="dont-rule-show-estimate"
            className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-5 py-3"
          >
            Run my real price for {school}
          </button>
          <button
            onClick={onConfirm}
            data-testid="dont-rule-confirm-remove"
            className="rounded-full border border-[#bfdbfe] text-slate-600 hover:text-red-600 text-sm px-5 py-2.5"
          >
            Remove anyway
          </button>
        </div>
      </div>
    </div>
  );
}
